<template>
  <Dialog v-model:open="isOpen">
    <DialogContent class="sm:max-w-md">
      <DialogHeader>
        <DialogTitle class="flex items-center gap-2">
          <Sparkles class="h-5 w-5 text-primary" />
          What's New in v{{ currentVersion }}
        </DialogTitle>
        <DialogDescription>
          Here's what's new in this update
        </DialogDescription>
      </DialogHeader>

      <div class="space-y-4 py-4">
        <div
          v-for="(highlight, index) in highlights"
          :key="index"
          class="flex items-start gap-3 p-3 rounded-lg bg-muted/50 border border-border"
        >
          <div class="flex-shrink-0 h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center">
            <component :is="getIcon(highlight.icon)" class="h-4 w-4 text-primary" />
          </div>
          <div class="flex-1 min-w-0">
            <div class="text-sm font-medium">{{ highlight.title }}</div>
            <div class="text-xs text-muted-foreground mt-0.5">{{ highlight.description }}</div>
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button @click="dismiss" class="w-full">
          Got it!
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import {
  Sparkles,
  Zap,
  Palette,
  Music,
  Monitor,
  Lightbulb,
  Settings,
  Rocket,
  Search,
  Library,
  Volume2,
  Image,
  Download
} from 'lucide-vue-next'
import { changelog, CURRENT_VERSION } from '@/data/changelog'
import { useAuthStore } from '@/stores/auth'

const STORAGE_KEY = 'ambi-whats-new-seen-version'
const COOKIE_CONSENT_KEY = 'cookie-consent'

const authStore = useAuthStore()
const isOpen = ref(false)
// Track if we've already shown/checked this session to prevent repeated popups
const hasCheckedThisSession = ref(false)

const currentVersion = computed(() => CURRENT_VERSION)

const highlights = computed(() => {
  const latest = changelog[0]
  return latest?.highlights || []
})

const getIcon = (icon: string) => {
  const icons: Record<string, any> = {
    sparkles: Sparkles,
    zap: Zap,
    palette: Palette,
    music: Music,
    monitor: Monitor,
    lightbulb: Lightbulb,
    settings: Settings,
    rocket: Rocket,
    search: Search,
    library: Library,
    volume: Volume2,
    image: Image,
    download: Download
  }
  return icons[icon] || Sparkles
}

const dismiss = () => {
  localStorage.setItem(STORAGE_KEY, CURRENT_VERSION)
  isOpen.value = false
}

const checkAndShow = () => {
  // Only check once per session to prevent repeated popups on tab switches
  if (hasCheckedThisSession.value) return
  
  // Only show if:
  // 1. User is logged in
  // 2. Cookie consent has been given
  // 3. User hasn't seen this version yet
  const hasConsent = localStorage.getItem(COOKIE_CONSENT_KEY) !== null
  const seenVersion = localStorage.getItem(STORAGE_KEY)
  
  if (authStore.user && hasConsent && seenVersion !== CURRENT_VERSION) {
    // Mark as seen immediately to prevent re-showing
    localStorage.setItem(STORAGE_KEY, CURRENT_VERSION)
    isOpen.value = true
  }
  
  hasCheckedThisSession.value = true
}

onMounted(() => {
  // Small delay to let the app settle before showing
  setTimeout(checkAndShow, 500)
})

// Also watch for auth changes (user logs in after page load)
watch(() => authStore.user, (user) => {
  if (user) {
    // Small delay after login to check
    setTimeout(checkAndShow, 300)
  }
})

// Expose for manual triggering (e.g., from settings)
defineExpose({
  show: () => { isOpen.value = true }
})
</script>
